import { CircleAlert } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { cn } from '../lib/cn';

/** Atributos que o controle recebe do `Field` (espalhe no Input/NativeSelect/DateInput/Textarea…). */
export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  'aria-required'?: true;
}

export interface FieldProps {
  /** Preserve an existing control ID when forms or integrations already reference it. */
  id?: string;
  label: ReactNode;
  /** Ajuda persistente (não é placeholder). */
  description?: ReactNode;
  /** Mensagem de erro; presença liga `aria-invalid`. Texto + ícone, nunca só cor. */
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: (control: FieldControlProps) => ReactNode;
}

/**
 * Rótulo + controle + ajuda + erro com a ligação de acessibilidade pronta (label/for, aria-describedby, aria-invalid).
 * Uso: `<Field label="E-mail">{(c) => <Input {...c} />}</Field>`. Sem strings fixas: tudo vem por props (i18n no app).
 */
export function Field({
  id: providedId,
  label,
  description,
  error,
  required = false,
  className,
  children,
}: FieldProps) {
  const uid = useId();
  const id = providedId ?? `field-${uid}`;
  const descId = description ? `${id}-desc` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [descId, errorId].filter(Boolean).join(' ') || undefined;

  const control: FieldControlProps = {
    id,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(error ? { 'aria-invalid': true as const } : {}),
    // Só aria-required: o `required` nativo abriria o balão de validação do navegador (sem i18n); valide com zod/RHF.
    ...(required ? { 'aria-required': true as const } : {}),
  };

  return (
    <div data-slot="field" className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-body-sm font-medium text-foreground">
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-danger-subtle-foreground">
            *
          </span>
        ) : null}
      </label>
      {children(control)}
      {description ? (
        <p id={descId} className="text-caption text-muted-foreground">
          {description}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          role="alert"
          className="flex items-start gap-1 text-caption font-medium text-danger-subtle-foreground"
        >
          <CircleAlert aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}
